import { useEffect, useRef, useState } from "react";
import { Package, AlertCircle, Search, Hash } from "lucide-react";
import { useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { TrackingResult } from "@shared-core/data/api/tracking-types";
import { Button } from "../app/components/primitives/button";
import { Card, CardContent } from "../app/components/primitives/card";
import { TrackingResultCard } from "../app/components/domain/delivery/TrackingResultCard";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type SearchMode = "consignment" | "order";

type LookupState =
  | { type: "idle" }
  | { type: "loading" }
  | { type: "results"; data: TrackingResult[] }
  | { type: "not_found" }
  | { type: "error"; message: string };

export function TrackingPage() {
  const dataAdapter = useDataAdapter();
  const [searchParams] = useSearchParams();

  // Search mode: "order" when arriving from an order card, else "consignment"
  const [mode, setMode] = useState<SearchMode>(
    searchParams.get("order_no") ? "order" : "consignment",
  );

  // Consignment form state
  const [reference, setReference] = useState("");
  const [courier, setCourier] = useState<"dpd" | "dx">("dpd");

  // Order form state
  const [orderNo, setOrderNo] = useState(searchParams.get("order_no") ?? "");

  const [state, setState] = useState<LookupState>({ type: "idle" });

  // Auto-search when arriving with ?order_no=
  const didAutoSearch = useRef(false);
  useEffect(() => {
    const paramOrderNo = searchParams.get("order_no");
    if (paramOrderNo && !didAutoSearch.current) {
      didAutoSearch.current = true;
      setMode("order");
      setOrderNo(paramOrderNo);
      handleOrderLookup(paramOrderNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleConsignmentLookup(e: React.FormEvent) {
    e.preventDefault();
    const ref = reference.trim();
    if (!ref) return;

    setState({ type: "loading" });
    try {
      const result = await dataAdapter.lookupTracking(ref, courier);
      setState({ type: "results", data: [result] });
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 404) {
        setState({ type: "not_found" });
      } else {
        setState({
          type: "error",
          message: "Could not reach the courier API. Try again shortly.",
        });
      }
    }
  }

  async function handleOrderLookup(no?: string) {
    const num = (no ?? orderNo).trim();
    if (!num) return;

    setState({ type: "loading" });
    try {
      const results = await dataAdapter.getOrderTracking(num);
      if (results.length === 0) {
        setState({ type: "not_found" });
      } else {
        setState({ type: "results", data: results });
      }
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 404) {
        setState({ type: "not_found" });
      } else {
        setState({
          type: "error",
          message: "Could not reach the courier API. Try again shortly.",
        });
      }
    }
  }

  function handleOrderFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    handleOrderLookup();
  }

  function switchMode(next: SearchMode) {
    setMode(next);
    setState({ type: "idle" });
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold">Delivery tracking</h1>
        <p className="text-muted-foreground mt-1">
          Track a consignment by reference or look up all shipments on an order.
        </p>
      </div>

      {/* Mode tabs */}
      <div className="flex gap-1 border-b border-border">
        <button
          onClick={() => switchMode("consignment")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            mode === "consignment"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          By consignment ref
        </button>
        <button
          onClick={() => switchMode("order")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            mode === "order"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          By order number
        </button>
      </div>

      {/* Search form */}
      <Card>
        <CardContent className="pt-5">
          {mode === "consignment" ? (
            <form onSubmit={handleConsignmentLookup} className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <label
                    htmlFor="reference"
                    className="block text-sm font-medium mb-1.5"
                  >
                    Consignment reference
                  </label>
                  <input
                    id="reference"
                    type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="e.g. 15551234567890"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label
                    htmlFor="courier"
                    className="block text-sm font-medium mb-1.5"
                  >
                    Courier
                  </label>
                  <select
                    id="courier"
                    value={courier}
                    onChange={(e) => setCourier(e.target.value as "dpd" | "dx")}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="dpd">DPD</option>
                    <option value="dx">DX</option>
                  </select>
                </div>
              </div>
              <Button
                type="submit"
                disabled={!reference.trim() || state.type === "loading"}
                className="flex items-center gap-2"
              >
                <Search className="size-4" />
                {state.type === "loading" ? "Looking up…" : "Look up"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleOrderFormSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="order-no"
                  className="block text-sm font-medium mb-1.5"
                >
                  Order number
                </label>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <input
                      id="order-no"
                      type="text"
                      value={orderNo}
                      onChange={(e) => setOrderNo(e.target.value)}
                      placeholder="e.g. 12345"
                      className="w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      autoComplete="off"
                      autoFocus={!!searchParams.get("order_no")}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={!orderNo.trim() || state.type === "loading"}
                    className="flex items-center gap-2"
                  >
                    <Search className="size-4" />
                    {state.type === "loading" ? "Looking up…" : "Look up"}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Finds all despatched consignments linked to this order in
                Tharstern.
              </p>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {state.type === "results" && (
        <div className="space-y-4">
          {state.data.length > 1 && (
            <p className="text-sm text-muted-foreground">
              {state.data.length} consignments found
            </p>
          )}
          {state.data.map((result) => (
            <TrackingResultCard
              key={result.tracking_reference}
              result={result}
            />
          ))}
        </div>
      )}

      {state.type === "not_found" && (
        <div className="flex items-start gap-3 rounded-md border border-border bg-surface p-4 text-sm">
          <Package className="size-4 shrink-0 mt-0.5 text-muted-foreground" />
          <div>
            <p className="font-medium">
              {mode === "order"
                ? "No despatched consignments found"
                : "Reference not found"}
            </p>
            <p className="text-muted-foreground mt-0.5">
              {mode === "order"
                ? "No consignments have been despatched for this order yet, or the order number isn't recognised."
                : "The courier doesn't recognise this reference. Check it's correct and that the parcel has been booked in."}
            </p>
          </div>
        </div>
      )}

      {state.type === "error" && (
        <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm">
          <AlertCircle className="size-4 shrink-0 mt-0.5 text-red-500" />
          <div>
            <p className="font-medium text-red-700">Courier API unavailable</p>
            <p className="text-red-600 mt-0.5">{state.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}
